const OPTIMIZABLE_TYPES = ['image/jpeg', 'image/png']
const SKIP_BELOW_BYTES = 1024 * 1024

export interface OptimizeImageOptions {
  maxBytes?: number
  maxDimension?: number
  quality?: number
  square?: boolean
  // Re-encodes JPEGs even when already small so EXIF (GPS, device, timestamps) is dropped
  stripMetadata?: boolean
}

const DEFAULT_MAX_DIMENSION = 2000
const DEFAULT_QUALITY = 0.85

// Each step trades more quality or resolution for a smaller file, used only while above maxBytes
const FALLBACK_STEPS = [
  { quality: 0.7, scale: 1 },
  { quality: 0.55, scale: 1 },
  { quality: 0.7, scale: 0.75 },
  { quality: 0.6, scale: 0.5 },
]

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))

const toJpegName = (name: string) => name.replace(/\.[^.]+$/, '') + '.jpg'

/**
 * Downscales and re-encodes large JPEG/PNG photos in the browser. With `maxBytes` it keeps
 * lowering quality and size until the result fits, returning the smallest attempt otherwise.
 * Never throws — returns the original file when it can't be decoded or isn't made smaller.
 */
export const optimizeImage = async (
  file: File,
  options: OptimizeImageOptions = {},
): Promise<File> => {
  const {
    maxBytes,
    maxDimension = DEFAULT_MAX_DIMENSION,
    quality = DEFAULT_QUALITY,
    square = false,
    stripMetadata = false,
  } = options

  if (!OPTIMIZABLE_TYPES.includes(file.type)) return file

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const { height, width } = bitmap

    const needsResize = Math.max(width, height) > maxDimension
    const needsCrop = square && width !== height
    const needsStrip = stripMetadata && file.type === 'image/jpeg'

    if (!needsResize && !needsCrop && !needsStrip && file.size <= SKIP_BELOW_BYTES) {
      bitmap.close()
      return file
    }

    const cropSize = square ? Math.min(width, height) : 0
    const sourceWidth = square ? cropSize : width
    const sourceHeight = square ? cropSize : height
    const sourceX = square ? (width - cropSize) / 2 : 0
    const sourceY = square ? (height - cropSize) / 2 : 0
    const baseScale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))

    const render = async (scale: number, jpegQuality: number): Promise<Blob | null> => {
      const targetWidth = Math.max(1, Math.round(sourceWidth * baseScale * scale))
      const targetHeight = Math.max(1, Math.round(sourceHeight * baseScale * scale))

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight

      const context = canvas.getContext('2d')

      if (!context) return null

      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, targetWidth, targetHeight)
      context.drawImage(
        bitmap,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        targetWidth,
        targetHeight,
      )

      return canvasToBlob(canvas, jpegQuality)
    }

    let best = await render(1, quality)

    if (maxBytes) {
      for (const step of FALLBACK_STEPS) {
        if (!best || best.size <= maxBytes) break

        const attempt = await render(step.scale, step.quality)

        if (attempt && attempt.size < best.size) best = attempt
      }
    }

    bitmap.close()

    if (!best || (!needsCrop && !needsStrip && best.size >= file.size)) return file

    return new File([best], toJpegName(file.name), {
      lastModified: file.lastModified,
      type: 'image/jpeg',
    })
  } catch {
    return file
  }
}
